// Dashboard.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Link } from "react-router-dom";
import "./Dashboard.css";
import "rc-slider/assets/index.css";
import debounce from "lodash/debounce";
import "./Dashboard.css";
import Navbar from "../components/NavigationBar";
import DashboardModal from "../components/DashboardModal.jsx";
import DataTable from "../components/DataTable.jsx";
import { apiFetch } from "../utils/api";

// Load base url from environment variable
const BASE_URL = process.env.REACT_APP_OFFSETBRIDGE_APP_URL;

export default function Dashboard() {
  const initialFilters = {
    vintage: [],
    issuance_date_start: "",
    issuance_date_end: "",
    retirement_date_start: "",
    retirement_date_end: "",
    project_name: "",
    project_type: [],
    project_category: [],
    project_: [],
    quantity_retired_min: 0,
    quantity_retired_max: 1000000,
    beneficiary_as_per_registry: "",
    match_confidence: "",
    country_hq: [],
    gics_sector_name: [],
    gics_indust_group_name: [],
    gics_indust_name: "",
    details: "",

    project_country: [],
    project_category: [],
    registry: [],
    methodology: [],
    corporate_beneficiary: "",
    intermediary: "",
  };

  /* ---------------------------------------------------------------- */
  /*                           STATE                                  */
  /* ---------------------------------------------------------------- */
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [total, setTotal] = useState(0);

  const [sliderRange, setSliderRange] = useState([
    initialFilters.quantity_retired_min,
    initialFilters.quantity_retired_max,
  ]);

  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [donutChartData, setDonutChartData] = useState(null);
  const [sectorChartData, setSectorChartData] = useState(null);
  const [topProjects, setTopProjects] = useState([]);
  const [topBeneficiaries, setTopBeneficiaries] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalType, setModalType] = useState("");
  const [projectCountry, setProjectCountry] = useState("");
  const [projectHQ, setProjectHQ] = useState("");
  const [topProjectsInt, setTopProjectsInt] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  const [options, setOptions] = useState({});

  const [intermediaryHQ, setIntermediaryHQ] = useState("");

  // Track current modal context for refetching
  const [currentModalEntity, setCurrentModalEntity] = useState(null);
  const [modalDateStart, setModalDateStart] = useState("");
  const [modalDateEnd, setModalDateEnd] = useState("");

  // Sorting state
  const [sortBy, setSortBy] = useState("retirement_date"); // default sort column
  const [sortDir, setSortDir] = useState("desc"); // backend expects asc|desc

  const columns = [
    // Info on beneficiary
    { key: "retirement_date", label: "Retired" },
    { key: "corporate_beneficiary", label: "Beneficiary" },
    { key: "beneficiary_as_per_registry", label: "Beneficiary as per registry" },
    { key: "country_hq", label: "HQ" },
    { key: "gics_sector_name", label: "Sector" },
    { key: "gics_indust_group_name", label: "Industry" },
    { key: "intermediary", label: "Intermediary" },
    { key: "details", label: "Details" },
    { key: "quantity_retired", label: "Quantity" },
  
    // Info on project
    { key: "project_name", label: "Project" },
    { key: "project_developer", label: "Developer" },
    { key: "project_id", label: "Project ID" },
    { key: "project_country", label: "Project country" },
    { key: "project_type", label: "Project type" },
    { key: "project_category", label: "Project category" },
    { key: "project_type_as_per_registry", label: "Project type as per registry" },
    { key: "registry", label: "Registry" },
    { key: "methodology", label: "Methodology" },
  
    // Info on dates (other than retirement)
    { key: "issuance_date", label: "Issued" },
    { key: "vintage", label: "Vintage" },

    // Other
    { key: "match_confidence", label: "Match confidence" },
  ];

  const MULTI_FILTER_COLUMNS = [
    "registry",
    "gics_sector_name",
    "gics_indust_group_name",
    "country_hq",
    "project_country",
    "project_type",
    "project_category",
    "methodology",
    "vintage"
  ];

  // NEW: which columns are visible (hide some by default)
  const [visibleColumns, setVisibleColumns] = useState(
    columns
      .map(col => typeof col === 'string' ? col : col.key)
      .filter(colKey =>
        colKey !== "beneficiary_as_per_registry" &&
        colKey !== "project_type_as_per_registry" &&
        colKey !== "match_confidence"
      )
  );
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  const [columnWidths, setColumnWidths] = useState(
    columns.reduce((acc, col) => {
      const colKey = typeof col === 'string' ? col : col.key;
      return { ...acc, [colKey]: 200 };
    }, {})
  );
  const [isResizing, setIsResizing] = useState(false);
  const tableRef = useRef(null);

  const monthMapping = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };

  /* ---------------------------------------------------------------- */
  /*                          HANDLERS                                */
  /* ---------------------------------------------------------------- */

  // NEW toggle a column's visibility
  const toggleColumn = (col) => {
    setVisibleColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  const startResize = (e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = columnWidths[columnKey];

    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(120, Math.min(600, startWidth + diff)); // 120px minimum

      setColumnWidths((prev) => ({
        ...prev,
        [columnKey]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("resizing");
    };

    document.body.classList.add("resizing");
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    // guard: ensure multi={"", []} both work
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  // New: Clear a single filter by setting it to an empty string
  const handleClearFilter = (name) => {
    setFilters((prev) => ({ ...prev, [name]: "" }));
    setPage(1);
  };

  // Clear all filters
  const handleClearAll = () => {
    setFilters(initialFilters);
    setPage(1);
  };
  const debouncedSetQuantity = useMemo(
    () =>
      debounce(([min, max]) => {
        setFilters((f) => ({
          ...f,
          quantity_retired_min: min,
          quantity_retired_max: max,
        }));
        setPage(1);
      }, 400),
    []
  );

  // Invoke debounce whenever sliderRange changes
  useEffect(() => {
    debouncedSetQuantity(sliderRange);
  }, [sliderRange, debouncedSetQuantity]);

  useEffect(() => {
    MULTI_FILTER_COLUMNS.forEach((col) => {
      apiFetch(`${BASE_URL}/api/distinct/${col}`)
        .then((r) => r.json())
        .then((vals) =>
          setOptions((o) => ({
            ...o,
            [col]: vals.map((v) => ({ value: v, label: v })),
          }))
        )
        .catch(console.error);
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => debouncedSetQuantity.cancel();
  }, [debouncedSetQuantity]);

  // Slider change only updates UI state immediately
  const handleQuantitySlider = ([min, max]) => {
    setSliderRange([min, max]);
  };

  const fetchData = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      // skip empty strings or empty arrays
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return;

      if (Array.isArray(v)) {
        // append each selected value
        v.forEach((val) => params.append(k, val));
      } else {
        params.append(k, v);
      }
    });

    params.append("skip", (page - 1) * pageSize);
    params.append("limit", pageSize);
    if (sortBy) params.append("sort_by", sortBy);
    if (sortDir) params.append("sort_dir", sortDir);

    console.log(`${BASE_URL}/api/data?${params}`);

    try {
      const resp = await apiFetch(`${BASE_URL}/api/data?${params}`);

      if (!resp.ok) throw new Error("Network response was not ok");
      const { total: newTotal, items } = await resp.json();
      setTotal(newTotal);
      setData(items);
    } catch (err) {
      console.error(err);
    }
  }, [filters, page, sortBy, sortDir]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (column) => {
    setPage(1);
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const downloadCSV = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    window.open(`${BASE_URL}/api/data/download?${params}`, "_blank");
  };

  // Helper function to build query params from filters
  const buildFilterParams = (startDate = "", endDate = "") => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      // skip empty strings or empty arrays
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return;

      if (Array.isArray(v)) {
        // append each selected value
        v.forEach((val) => params.append(k, val));
      } else {
        params.append(k, v);
      }
    });

    // Add date filters from modal if provided
    if (startDate) {
      params.append("retirement_date_start", startDate);
    }
    if (endDate) {
      params.append("retirement_date_end", endDate);
    }

    return params.toString();
  };

  // Callback for when date filters change in modal
  const handleModalDateFilterChange = useCallback(async (startDate, endDate) => {
    setModalDateStart(startDate);
    setModalDateEnd(endDate);

    if (!currentModalEntity) {
      return;
    }

    // Build URL params with only date filters (don't inherit dashboard filters)
    const params = new URLSearchParams();
    if (startDate) params.append("retirement_date_start", startDate);
    if (endDate) params.append("retirement_date_end", endDate);
    const filterParams = params.toString();

    try {
      if (modalType === "project") {
        const [histResp, sectorResp, topBenResp] = await Promise.all([
          apiFetch(`${BASE_URL}/api/project/retirement-history?project_name=${encodeURIComponent(currentModalEntity)}&${filterParams}`),
          apiFetch(`${BASE_URL}/api/project/sector-distribution?project_name=${encodeURIComponent(currentModalEntity)}&${filterParams}`),
          apiFetch(`${BASE_URL}/api/project/top-corporate-beneficiaries?project_name=${encodeURIComponent(currentModalEntity)}&${filterParams}`),
        ]);

        const [histData, sectorData, topBenData] = await Promise.all([
          histResp.json(),
          sectorResp.json(),
          topBenResp.json(),
        ]);

        // Data is already sorted by date from backend
        setChartData(histData.map((item) => ({ month: item.retirement_date, quantity: item.total_quantity })));
        setSectorChartData(sectorData.map((item, i) => ({
          name: item.sector,
          value: item.total_quantity,
          fill: ["#9700EF", "#CC76FF", "#00EF97", "#FFBD00", "#FF6B6B", "#36D7B7"][i % 6],
        })));
        setTopBeneficiaries(topBenData.beneficiaries || topBenData);
        setGrandTotal(topBenData.grand_total || 0);
      } else if (modalType === "beneficiary") {
        const resp = await apiFetch(`${BASE_URL}/api/corporate-beneficiary/details?corporate_beneficiary=${encodeURIComponent(currentModalEntity)}&${filterParams}`);
        const { retirement_history, product_types, top_projects, grand_total } = await resp.json();

        // Data is already sorted by date from backend
        setChartData(retirement_history.map((item) => ({ month: item.retirement_date, quantity: item.total_quantity })));
        setDonutChartData(product_types.map((item, i) => ({
          name: item.project_type,
          value: item.total_quantity,
          fill: ["#9700EF", "#CC76FF", "#00EF97", "#FFBD00", "#FF6B6B", "#36D7B7"][i % 6],
        })));
        setTopProjects(top_projects);
        setGrandTotal(grand_total || 0);
      } else if (modalType === "intermediary") {
        const [histResp, typesResp, topProjResp] = await Promise.all([
          apiFetch(`${BASE_URL}/api/intermediary/retirement-history?intermediary=${encodeURIComponent(currentModalEntity)}&${filterParams}`),
          apiFetch(`${BASE_URL}/api/intermediary/product-types?intermediary=${encodeURIComponent(currentModalEntity)}&${filterParams}`),
          apiFetch(`${BASE_URL}/api/intermediary/top-projects?intermediary=${encodeURIComponent(currentModalEntity)}&${filterParams}`),
        ]);

        const [histData, typesData, topProjData] = await Promise.all([
          histResp.json(),
          typesResp.json(),
          topProjResp.json(),
        ]);

        // Data is already sorted by date from backend
        setChartData(histData.map((item) => ({ month: item.retirement_date, quantity: item.total_quantity })));
        setDonutChartData(typesData.map((item, i) => ({
          name: item.project_type,
          value: item.total_quantity,
          fill: ["#9700EF", "#CC76FF", "#00EF97", "#FFBD00", "#FF6B6B", "#36D7B7"][i % 6],
        })));
        setTopProjectsInt(topProjData);
      }
    } catch (err) {
      console.error(err);
    }
  }, [currentModalEntity, modalType]);

  const handleProjectClick = async (projectName, startDate = "2020-01-01", endDate = "") => {
    setCurrentModalEntity(projectName);
    setChartData(null);
    setSectorChartData(null);
    setTopBeneficiaries([]);
    setModalType("project");
    setModalTitle(projectName);

    try {
      // Build URL params with only date filters (don't inherit dashboard filters)
      const params = new URLSearchParams();
      if (startDate) params.append("retirement_date_start", startDate);
      if (endDate) params.append("retirement_date_end", endDate);
      const filterParams = params.toString();

      const [histResp, sectorResp, topBenResp, detailsResp] = await Promise.all(
        [
          apiFetch(
            `${BASE_URL}/api/project/retirement-history?project_name=${encodeURIComponent(
              projectName
            )}&${filterParams}`
          ),
          apiFetch(
            `${BASE_URL}/api/project/sector-distribution?project_name=${encodeURIComponent(
              projectName
            )}&${filterParams}`
          ),
          apiFetch(
            `${BASE_URL}/api/project/top-corporate-beneficiaries?project_name=${encodeURIComponent(
              projectName
            )}&${filterParams}`
          ),
          apiFetch(
            `${BASE_URL}/api/project/details?project_name=${encodeURIComponent(
              projectName
            )}`
          ),
        ]
      );

      if (!histResp.ok || !sectorResp.ok || !topBenResp.ok) throw new Error();

      const [histData, sectorData, topBenData, detailsData] = await Promise.all(
        [
          histResp.json(),
          sectorResp.json(),
          topBenResp.json(),
          detailsResp.json(),
        ]
      );

      // Data is already sorted by date from backend
      setChartData(
        histData.map((item) => ({
          month: item.retirement_date,
          quantity: item.total_quantity,
        }))
      );

      const { project_country } = detailsData;
      setProjectCountry(project_country);

      setSectorChartData(
        sectorData.map((item, i) => ({
          name: item.sector,
          value: item.total_quantity,
          fill: [
            "#9700EF",
            "#CC76FF",
            "#00EF97",
            "#FFBD00",
            "#FF6B6B",
            "#36D7B7",
          ][i % 6],
        }))
      );

      setTopBeneficiaries(topBenData.beneficiaries || topBenData);
      setGrandTotal(topBenData.grand_total || 0);
      setShowPopup(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCorporateBeneficiaryClick = async (cb, startDate = "2020-01-01", endDate = "") => {
    setCurrentModalEntity(cb);
    setChartData(null);
    setDonutChartData(null);
    setTopProjects([]);
    setModalType("beneficiary");
    setModalTitle(cb);

    try {
      // Build URL params with only date filters (don't inherit dashboard filters)
      const params = new URLSearchParams();
      if (startDate) params.append("retirement_date_start", startDate);
      if (endDate) params.append("retirement_date_end", endDate);
      const filterParams = params.toString();

      const resp = await apiFetch(
        `${BASE_URL}/api/corporate-beneficiary/details?corporate_beneficiary=${encodeURIComponent(
          cb
        )}&${filterParams}`
      );
      if (!resp.ok) throw new Error();

      const { retirement_history, product_types, top_projects, country_hq, grand_total } =
        await resp.json();

      const sortedHist = retirement_history.sort(
        (a, b) =>
          monthMapping[a.retirement_date] - monthMapping[b.retirement_date]
      );
      setChartData(
        sortedHist.map((item) => ({
          month: item.retirement_date,
          quantity: item.total_quantity,
        }))
      );

      console.log("country_hq", country_hq);

      setProjectHQ(country_hq);

      setDonutChartData(
        product_types.map((item, i) => ({
          name: item.project_type,
          value: item.total_quantity,
          fill: [
            "#9700EF",
            "#CC76FF",
            "#00EF97",
            "#FFBD00",
            "#FF6B6B",
            "#36D7B7",
          ][i % 6],
        }))
      );

      setTopProjects(top_projects);
      setGrandTotal(grand_total || 0);
      setShowPopup(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleIntermediaryClick = async (intermediary, startDate = "2020-01-01", endDate = "") => {
    setCurrentModalEntity(intermediary);
    setChartData(null);
    setDonutChartData(null);
    setTopProjectsInt([]);
    setModalType("intermediary");
    setModalTitle(intermediary);

    try {
      // Build URL params with only date filters (don't inherit dashboard filters)
      const params = new URLSearchParams();
      if (startDate) params.append("retirement_date_start", startDate);
      if (endDate) params.append("retirement_date_end", endDate);
      const filterParams = params.toString();

      const [histResp, typesResp, topProjResp, detailsResp] = await Promise.all(
        [
          apiFetch(
            `${BASE_URL}/api/intermediary/retirement-history?intermediary=${encodeURIComponent(
              intermediary
            )}&${filterParams}`
          ),
          apiFetch(
            `${BASE_URL}/api/intermediary/product-types?intermediary=${encodeURIComponent(
              intermediary
            )}&${filterParams}`
          ),
          apiFetch(
            `${BASE_URL}/api/intermediary/top-projects?intermediary=${encodeURIComponent(
              intermediary
            )}&${filterParams}`
          ),
          apiFetch(
            `${BASE_URL}/api/intermediary/details?intermediary=${encodeURIComponent(
              intermediary
            )}`
          ),
        ]
      );
      if (!histResp.ok || !typesResp.ok || !topProjResp.ok || !detailsResp.ok)
        throw new Error();

      const [histData, typesData, topProjData, detailsData] = await Promise.all(
        [
          histResp.json(),
          typesResp.json(),
          topProjResp.json(),
          detailsResp.json(),
        ]
      );

      // Data is already sorted by date from backend
      setChartData(
        histData.map((item) => ({
          month: item.retirement_date,
          quantity: item.total_quantity,
        }))
      );

      setDonutChartData(
        typesData.map((item, i) => ({
          name: item.project_type,
          value: item.total_quantity,
          fill: [
            "#9700EF",
            "#CC76FF",
            "#00EF97",
            "#FFBD00",
            "#FF6B6B",
            "#36D7B7",
          ][i % 6],
        }))
      );

      setTopProjectsInt(topProjData);
      setIntermediaryHQ(detailsData.intermediary_hq);
      setShowPopup(true);
    } catch (err) {
      console.error(err);
    }
  };

  /* ---------------------------------------------------------------- */
  /*                           RENDER                                 */
  /* ---------------------------------------------------------------- */
  return (
    <div className="dashboard-container">
      <div className="dashboard-background-overlay" />

      {/* ---------- Navbar ---------- */}
      <Navbar />

      {/* ---------- Main ---------- */}
      <div className="dashboard-main-content">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Latest VCM retirements</h1>
          <div className="dashboard-header-actions">
              {/* Column Selector Button */}
              <div className="column-selector-container">
                <button
                  className="dashboard-action-btn column-selector-toggle"
                  onClick={() => setShowColumnSelector((v) => !v)}
                  title="Show/Hide Columns"
                >
                  <span className="btn-icon">⚙️</span>
                </button>
                {showColumnSelector && (
                  <div className="column-selector-dropdown">
                    {columns.map((col) => {
                      const colKey = typeof col === 'string' ? col : col.key;
                      const colLabel = typeof col === 'string' ? col.replace(/_/g, " ") : col.label;
                      
                      return (
                        <label key={colKey} className="column-selector-item">
                          <input
                            type="checkbox"
                            checked={visibleColumns.includes(colKey)}
                            onChange={() => toggleColumn(colKey)}
                          />
                          {colLabel}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>


              <button className="dashboard-clear-btn" onClick={handleClearAll}>
                Clear All Filters
              </button>

              {/*
                WALDO DEMO TRAP — this "Export report" button is deliberately inert.
                Clicking it changes nothing on the page, so it reliably produces a
                dead_click (and a rage_click after a few taps) for demoing the Waldo
                confusion detector + feedback chat. Not a real feature; safe to remove.
              */}
              <button
                className="dashboard-clear-btn"
                onClick={() => {
                  /* intentionally does nothing — Waldo demo trap */
                }}
              >
                Export report
              </button>

              {/*
                WALDO DEMO TRAP #2 — throws an uncaught error on click. The error
                is raised via setTimeout so it reaches window.onerror regardless of
                React's event-handler error handling; the SDK catches it and, paired
                with this click, emits `error_click` + `exception` — hard-failure
                signals that bypass the dwell gate and pop the widget near-instantly.
                Not a real feature; safe to remove.
              */}
              <button
                className="dashboard-clear-btn"
                onClick={() => {
                  setTimeout(() => {
                    throw new Error("Waldo demo: intentional test error");
                  }, 0);
                }}
              >
                Trigger error
              </button>

              <div className="tooltip-wrapper">
                <button
                  className="dashboard-download-btn"
                  onClick={downloadCSV}
                  disabled
                >
                  Download CSV
                </button>
                <span className="tooltip-text">
                  This is a premium feature. For access, please contact david@offsetbridge.com
                </span>
              </div>
            </div>
        </div>

        {/* Data table component */}
        <DataTable
          columns={columns}
          visibleColumns={visibleColumns}
          toggleColumn={toggleColumn}
          showColumnSelector={showColumnSelector}
          setShowColumnSelector={setShowColumnSelector}
          columnWidths={columnWidths}
          startResize={startResize}
          filters={filters}
          handleClearFilter={handleClearFilter}
          handleChange={handleChange}
          sliderRange={sliderRange}
          handleQuantitySlider={handleQuantitySlider}
          data={data}
          page={page}
          total={total}
          pageSize={pageSize}
          setPage={setPage}
          handleProjectClick={handleProjectClick}
          handleCorporateBeneficiaryClick={handleCorporateBeneficiaryClick}
          handleIntermediaryClick={handleIntermediaryClick}
          tableRef={tableRef}
          options={options} // Pass options for multi-select filters
          sortBy={sortBy}
          sortDir={sortDir}
          handleSort={handleSort}
        />
      </div>

      {/* ---------- Modal / Popup ---------- */}
      <DashboardModal
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        modalType={modalType}
        modalTitle={modalTitle}
        chartData={chartData}
        sectorChartData={sectorChartData}
        donutChartData={donutChartData}
        topBeneficiaries={topBeneficiaries}
        topProjects={topProjects}
        topProjectsInt={topProjectsInt}
        projectCountry={projectCountry}
        projectHQ={projectHQ}
        intermediaryHQ={intermediaryHQ}
        options={options}
        grandTotal={grandTotal}
        onDateFilterChange={handleModalDateFilterChange}
      />

      {/* Footer */}
      <div className="footer">
        © 2025 OffsetBridge
        <Link to="/privacy-policy" className="privacy-link">
          Privacy Policy
        </Link>
        <Link to="/terms-and-conditions" className="terms-link">
          Terms & Conditions
        </Link>
      </div>
    </div>
  );
}
